import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, inCubic, outCubic, outExpo, phase, settle, span } from '../curves.ts';

/*
 * La Barrena (models/ojos.ts, docs/sendas/artifice.md chapter 4): ice and old iron out of the mine's broken drills.
 * Idle (it creaks, the augers turn slowly), walk (heavy pillar strides, the body rocking), `raise` (its warning, a
 * loop: both augers lifted over its head and spinning fast; three seconds of it), attack (the slash: the augers brought
 * down and across at 0.15 s), `spin` (its other warning, a loop: the whole body turning on its legs, the augers out
 * level), hit, stun and die (it comes apart: the body sinks and tips, the augers drop).
 *
 * Conventions: −X on an arm raises it (forward and up), ±Z swings it out (+Z the left's outward, −Z the right's);
 * Y on an auger spins it about its own axis; X on a leg strides it.
 */

type B = 'root' | 'body' | 'head' | 'armL' | 'armR' | 'augL' | 'augR' | 'legL' | 'legR';

/** Both arms: `up` raised, `out` spread; the augers spun to `turn`. */
const arms = (b: Bones<B>, up: number, out: number, turn: number): void => {
  b.armL.rot(-up, 0, out);
  b.armR.rot(-up, 0, -out);
  b.augL.rot(0, turn, 0);
  b.augR.rot(0, -turn, 0);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 3.0, true, (b, t) => {
    const p = phase(t, 3.0) * TAU;
    b.body.pos(0, 0.015 * Math.sin(p), 0).rot(0.02 * Math.sin(p), 0, 0.015 * Math.sin(p * 0.5));
    b.head.rot(0, 0.2 * Math.sin(p * 0.5), 0);
    arms(b, 0.15 + 0.05 * Math.sin(p), 0.12, t * 2.2);
    b.legL.rot(0, 0, 0);
    b.legR.rot(0, 0, 0);
  });

const walk = (): Clip<B> =>
  clip<B>('walk', 1.4, true, (b, t) => {
    const p = phase(t, 1.4) * TAU;
    b.body.pos(0, 0.04 * Math.abs(Math.sin(p)), 0).rot(0.05, 0, 0.06 * Math.sin(p));
    b.legL.rot(0.4 * Math.sin(p), 0, 0);
    b.legR.rot(-0.4 * Math.sin(p), 0, 0);
    arms(b, 0.25 - 0.15 * Math.sin(p), 0.18, t * 3);
    b.head.rot(0.05, 0, 0);
  });

/** Its slash's warning: the augers lifted high and spinning fast. */
const raise = (): Clip<B> =>
  clip<B>('raise', 0.6, true, (b, t) => {
    const p = phase(t, 0.6) * TAU;
    const sh = Math.sin(p * 3) * 0.04;
    b.body.rot(-0.15, 0, sh).pos(0, 0.05, -0.05);
    b.head.rot(-0.15, 0, 0);
    arms(b, 2.4 + sh, 0.4, t * 26);
  });

/** The slash: down and across at 0.15 s. */
const attack = (): Clip<B> =>
  clip<B>('attack', 0.9, false, (b, t) => {
    const k = span(t, 0, 0.15, outExpo) * (1 - span(t, 0.4, 0.9, outCubic));
    const down = span(t, 0.05, 0.15, inCubic);
    b.body.rot(0.25 * k, 0.3 * k, 0).pos(0, -0.04 * k, 0.12 * k + 0.03 * settle(t - 0.15, 4, 7));
    arms(b, 2.4 * (1 - down) - 0.2 * k, 0.4 * (1 - k) - 0.3 * k, t * 20);
    b.legL.rot(-0.2 * k, 0, 0);
    b.legR.rot(0.2 * k, 0, 0);
  });

/** Its spin's warning: turning on its legs, the augers out level. */
const spin = (): Clip<B> =>
  clip<B>('spin', 0.8, true, (b, t) => {
    const p = phase(t, 0.8);
    b.body.rot(0, p * TAU, 0).pos(0, 0.03, 0);
    arms(b, 1.4, 1.2, t * 30);
  });

const hit = (): Clip<B> =>
  clip<B>('hit', 0.4, false, (b, t) => {
    const k = settle(t, 3, 8);
    b.body.rot(-0.12 * k, 0, 0.06 * k).pos(0, 0, -0.05 * k);
    arms(b, 0.3 + 0.3 * k, 0.3, t * 4);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    b.body.rot(0.1, 0, 0.1 * Math.sin(p)).pos(0, -0.06, 0);
    arms(b, -0.1, 0.25, 0);
  });

const die = (): Clip<B> =>
  clip<B>('die', 2.0, false, (b, t) => {
    const k = span(t, 0, 1.3, outCubic);
    b.body.pos(0, -0.55 * k, 0.1 * k).rot(0.5 * k, 0, 0.3 * k);
    b.head.rot(0.6 * k, 0, 0.3 * k);
    arms(b, -0.5 * k, 0.9 * k, 0);
    b.legL.rot(0, 0, 0.3 * k);
    b.legR.rot(0, 0, -0.3 * k);
  });

export const barrenaClips = (): Clip[] => [idle(), walk(), raise(), attack(), spin(), hit(), stun(), die()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { barrena: barrenaClips };
